import React from "react";
import { useTranslation } from "react-i18next";
import { useEBoekhoudenStatusPolled } from "../../hooks/useIntegrationStatus";

type Status = { state?: string; enabled?: boolean; checkedAt?: string | null; detail?: string | null };

/** "OK", "checking", "off" or a problem, for the dot and its tooltip. */
export const useEBoekhoudenStatus = (enabled = true) => {
  const snapshot = useEBoekhoudenStatusPolled(enabled) as { loading?: boolean; error?: string | null; data?: Status | null } | null;
  const data = snapshot?.data ?? null;
  const checking = Boolean(snapshot?.loading) && !data;
  const state = snapshot?.error ? "ERROR" : data?.state ?? null;
  return { checking, state, data };
};

export const eBoekhoudenStateLabel = (t: (key: string, options?: Record<string, unknown>) => string, state: string | null, checking: boolean) => {
  if (checking || !state) return t("finance.eboekhouden.status.checking", { defaultValue: "Checking e-Boekhouden…" });
  switch (state) {
    case "OK": return t("finance.eboekhouden.status.ok", { defaultValue: "e-Boekhouden connected" });
    case "DISABLED": return t("finance.eboekhouden.status.disabled", { defaultValue: "e-Boekhouden switched off" });
    case "NOT_CONFIGURED": return t("finance.eboekhouden.status.not_configured", { defaultValue: "e-Boekhouden not set up" });
    case "UNREACHABLE": return t("finance.eboekhouden.status.unreachable", { defaultValue: "e-Boekhouden unreachable" });
    case "REJECTED": return t("finance.eboekhouden.status.rejected", { defaultValue: "e-Boekhouden refused the connection" });
    default: return t("finance.eboekhouden.status.error", { defaultValue: "e-Boekhouden error" });
  }
};

export const eBoekhoudenDotClass = (state: string | null, checking: boolean) => {
  if (checking || !state) return "bg-amber-500 shadow-[0_0_0_4px_rgba(245,158,11,0.18)]";
  if (state === "OK") return "bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.18)]";
  if (state === "DISABLED" || state === "NOT_CONFIGURED") return "bg-slate-400 shadow-[0_0_0_4px_rgba(148,163,184,0.2)]";
  return "bg-rose-500 shadow-[0_0_0_4px_rgba(244,63,94,0.18)]";
};

/** Status dot for e-Boekhouden, like the ones for Gitea, Noctis and Mailchimp. */
export default function EBoekhoudenStatusDot({ showLabel = true }: { showLabel?: boolean }) {
  const { t } = useTranslation();
  const { checking, state } = useEBoekhoudenStatus(true);
  const title = eBoekhoudenStateLabel(t, state, checking);
  return (
    <div className="flex items-center gap-2" title={title} aria-label={title} data-eboekhouden-state={state ?? "CHECKING"}>
      <span className={`h-3 w-3 rounded-full ${eBoekhoudenDotClass(state, checking)}`} />
      {showLabel && (
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-600 dark:text-gray-300">e-Boekhouden</span>
      )}
    </div>
  );
}
